import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  InfoIcon,
  Settings,
  Shield,
  Copy,
  Download,
  RefreshCw,
  Clock,
} from "lucide-react";
import Sidebar from "./Sidebar";
import SEOHelmet from "../../../components/SEOHelmet";

export default function PasswordHistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("date"); // date or password
  const [sortOrder, setSortOrder] = useState("desc"); // asc or desc
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [exportedIndex, setExportedIndex] = useState(null);

  const menuItems = [
    { icon: RefreshCw, label: "Generator", path: "/passwordgenerator" },
    { icon: Clock, label: "Password History", path: "/passwordhistory" },
    { icon: Shield, label: "Advanced Password", path: "/advancedpassword" },
    { icon: Settings, label: "Settings", path: "/settings" },
    { icon: InfoIcon, label: "About", path: "/about" },
  ];

  useEffect(() => {
    const savedHistory = JSON.parse(localStorage.getItem("passwordHistory")) || [];
    setHistory(savedHistory);
  }, []);

  const clearHistory = () => {
    localStorage.removeItem("passwordHistory");
    setHistory([]);
  };

  const copyPassword = (pwd, index) => {
    navigator.clipboard.writeText(pwd);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const exportSingle = (pwd, index) => {
    const csvHeader = "Password,Date & Time\n";
    const csvRow = `"${pwd}","${new Date().toLocaleString("en-GB",{ hour12:false })}"\n`;
    const csvContent = "data:text/csv;charset=utf-8," + csvHeader + csvRow;
    const encodedUri = encodeURI(csvContent);

    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "password.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setExportedIndex(index);
    setTimeout(() => setExportedIndex(null), 2000);
  };

  const exportAll = () => {
    if (!history.length) return;
    const csvHeader = "Password,Date & Time\n";
    const csvRows = history
      .map(
        (item) =>
          `"${item.password}","${item.date || new Date().toLocaleString("en-GB",{ hour12:false })}"`
      )
      .join("\n");
    const csvContent = "data:text/csv;charset=utf-8," + csvHeader + csvRows;
    const encodedUri = encodeURI(csvContent);

    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "passwords.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter based on search
  const filteredHistory = history.filter((item) =>
    item.password.toLowerCase().includes(search.toLowerCase())
  );

  // Sort filtered history
  const sortedHistory = [...filteredHistory].sort((a, b) => {
    if (sortField === "password") {
      if (sortOrder === "asc") return a.password.localeCompare(b.password);
      return b.password.localeCompare(a.password);
    } else {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (sortOrder === "asc") return dateA - dateB;
      return dateB - dateA;
    }
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-inter text-gray-800">
      {/* Sidebar */}
      <Sidebar menuItems={menuItems} />

      <SEOHelmet
  title="Password History"
  description="View and manage all your previously generated passwords securely with our Password History tool. Keep track of your strong passwords easily."
  keywords="password history, saved passwords, password manager, password tracker, secure passwords"
  url="https://omkarmore.com/passwordhistory"
  image="https://omkarmore.com/LOGO_512.png"
/>


      {/* Main content */}
      <div className="flex-1 p-6 md:ml-64">
        {/* Back button */}
        <div className="sticky top-0 bg-gray-50 z-20 py-4 flex justify-center md:justify-start px-6 shadow-sm mb-4">
          <button
            onClick={() => navigate("/tools")}
            className="flex items-center gap-2 text-cyan-700 font-medium hover:text-cyan-600 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Tools
          </button>
        </div>

        {/* Header + Search */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Password History</h1>
          <input
            type="text"
            placeholder="Search passwords..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>

        {/* Export All Button */}
        {history.length > 0 && (
          <div className="flex justify-end mb-2">
            <button
              onClick={exportAll}
              className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Download size={16} /> Export All
            </button>
          </div>
        )}

        {/* Table */}
        {sortedHistory.length === 0 ? (
          <p className="text-gray-500">No passwords found.</p>
        ) : (
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead>
                <tr className="bg-cyan-50">
                  <th className="py-2 px-4 text-left">#</th>
                  <th
                    className="py-2 px-4 text-left cursor-pointer"
                    onClick={() => toggleSort("password")}
                  >
                    Password {sortField === "password" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th
                    className="py-2 px-4 text-left cursor-pointer"
                    onClick={() => toggleSort("date")}
                  >
                    Date {sortField === "date" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th className="py-2 px-4 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedHistory.map((item, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                  >
                    <td className="py-2 px-4">{index + 1}</td>
                    <td className="py-2 px-4 font-mono">{item.password}</td>
                    <td className="py-2 px-4 text-gray-500">{item.date}</td>
                    <td className="py-2 px-4 flex items-center gap-2">
                      <button
                        onClick={() => copyPassword(item.password, index)}
                        className="px-2 py-1 bg-cyan-500 text-white rounded hover:bg-cyan-600 transition text-sm flex items-center gap-1"
                      >
                        <Copy size={14} /> Copy
                      </button>
                      {copiedIndex === index && (
                        <span className="text-green-600 text-sm whitespace-nowrap">Copied!</span>
                      )}

                      <button
                        onClick={() => exportSingle(item.password, index)}
                        className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition text-sm flex items-center gap-1"
                      >
                        <Download size={14} /> Export
                      </button>
                      {exportedIndex === index && (
                        <span className="text-green-600 text-sm whitespace-nowrap">Exported!</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Clear History */}
        {history.length > 0 && (
          <div className="flex justify-end mt-4">
            <button
              onClick={clearHistory}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
            >
              Clear History
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
